import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { MOBILE_BREAKPOINT } from '../../Store';
import { Dialog, DialogWindow, useDialogManager } from '../dialogs/Dialogs';
import { Header } from './Header';
import { ImagesContainer } from './ImagesContainer';
import { Controls } from './Controls';
import { useAIProductStagingData } from './useAIProductStagingData';
import { useMobileFixedDropdopwnOpen, MobileDropdownProvider } from './FixedDropdown';
import { WebcamCaptureDialog } from './WebcamCaptureDialog';

const StyledDialogWindow = styled(DialogWindow)`
	max-width: 100%;
	max-height: 100%;
	height: 100%;
	width: 100%;
	flex-basis: 100%;
	padding: 0;
	margin: 0;
	border-radius: 0;
`;

// Main entry point for AI Product Staging dialog
// This component orchestrates Header, Canvas, Controls, and OptionsPanel with full state management

interface AIProductStagingProps {
	onClose: () => void;
	initialMode?: 'product-staging' | 'virtual-tryon';
}

export const AIProductStaging: React.FC<AIProductStagingProps> = ({ onClose, initialMode }) => {
	const { T_static, isAiProductStagingEnabled, isAiVtoEnabled } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);
	const isDropdownOpen = useMobileFixedDropdopwnOpen();
	const { showDialog, closeDialog } = useDialogManager();

	// Load data asynchronously and get all state from hook
	const {
		mood,
		person,
		pose,
		background,
		surface,
		effect,
		isLoadingPreset,
		isLoadingGenerate,
		hasGenerated,
		activePanel,
		setActivePanel,
		modifiedImages,
		prompt,
		setPrompt,
		selectedBackground,
		selectedPerson,
		selectedPose,
		selectedSurface,
		selectedMood,
		selectedEffect,
		imageUrl,
		setImageUrl,
		inputImage,
		setInputImage,
		handleSelect,
		handleGenerate,
		handleUpload,
		handleBackToInput,
		handleDeleteVariant,
		hasUserUpload,
		handleWebcamCapture,
		modalType,
		setModalType,
		allowCustomBackground,
	} = useAIProductStagingData(initialMode);

	// Auto-switch to available mode if current mode has no rules
	useEffect(() => {
		if (modalType === 'product-staging' && !isAiProductStagingEnabled && isAiVtoEnabled) {
			setModalType('virtual-tryon');
		} else if (modalType === 'virtual-tryon' && !isAiVtoEnabled && isAiProductStagingEnabled) {
			setModalType('product-staging');
		}
	}, [modalType, isAiProductStagingEnabled, isAiVtoEnabled, setModalType]);

	// Handlers
	const handleWebcamOpen = useCallback(() => {
		showDialog(
			'webcam-capture',
			<WebcamCaptureDialog
				onCapture={(file) => {
					handleWebcamCapture(file);
					closeDialog('webcam-capture');
				}}
				onClose={() => closeDialog('webcam-capture')}
			/>
		);
	}, [showDialog, closeDialog, handleWebcamCapture]);

	const handleModeChange = useCallback(
		(mode: 'designer' | 'staging' | 'tryon') => {
			if (mode === 'designer') {
				// Close the dialog when switching to designer mode
				onClose();
			} else if (mode === 'staging') {
				setModalType('product-staging');
			} else if (mode === 'tryon') {
				setModalType('virtual-tryon');
			}
		},
		[onClose, setModalType]
	);

	const handlePanelToggle = useCallback(
		(panelId: string | null) => {
			if (activePanel === panelId) {
				setActivePanel(null);
			} else {
				setActivePanel(panelId);
			}
		},
		[activePanel, setActivePanel]
	);

	// Get selected option details for display
	const currentBg = background.presets.find((b) => b.id === selectedBackground);
	const currentSurface = surface.presets.find((s) => s.id === selectedSurface);
	const currentMood = mood.presets.find((m) => m.id === selectedMood);
	const currentEffect = effect.presets.find((e) => e.id === selectedEffect);
	const currentPerson = person.presets.find((p) => p.id === selectedPerson);
	const currentPose = pose.presets.find((p) => p.id === selectedPose);

	// Determine current mode for Header
	const currentMode = modalType === 'product-staging' ? 'staging' : 'tryon';

	return (
		<MobileDropdownProvider>
			<Dialog
				windowDecorator={StyledDialogWindow}
				showCloseButton={!isLoadingGenerate}
				onClose={onClose}
				fullscreen
				maxHeight="calc(100vh)"
				overflow="auto"
				padding="0"
			>
				<ContentWrapper $hasActivePanel={activePanel !== null}>
				{/* tailwind: h-screen bg-gray-50 flex flex-col */}

				{/* Header with mode switching */}
				<Header
					currentMode={currentMode}
					onModeChange={handleModeChange}
					onClose={onClose}
					isLoadingGenerate={isLoadingGenerate}
					isInert={isDropdownOpen}
					isProductStagingEnabled={isAiProductStagingEnabled}
					isVtoEnabled={isAiVtoEnabled}
				/>

				<ImagesContainer
					hasGenerated={hasGenerated}
					variants={hasGenerated ? modifiedImages.map((url, id) => ({ id, url })) : []}
					onVariantClick={(variantId) => {
						// No action needed - 3D view is always shown before generation
					}}
					selectedVariantId={undefined}
					isActive3D={!inputImage}
					isLoadingGenerate={isLoadingGenerate}
					isInert={isDropdownOpen}
					onUseAsInput={setInputImage}
					inputImage={inputImage}
					onClearInputImage={() => setInputImage(null)}
					onBackToInput={handleBackToInput}
					onDeleteVariant={handleDeleteVariant}
				/>
				<ControlsAndOptionsWrapper>
					<Controls
						modalType={modalType}
						prompt={prompt}
						onPromptChange={setPrompt}
						activePanel={activePanel}
						onPanelToggle={handlePanelToggle}
						onGenerate={handleGenerate}
						isLoadingGenerate={isLoadingGenerate}
						selectedOptions={{
							background: currentBg,
							surface: currentSurface,
							mood: currentMood,
							effect: currentEffect,
							person: currentPerson,
							pose: currentPose,
						}}
						options={{
							background: background,
							surface: surface,
							mood: mood,
							effect: effect,
							person: person,
							pose: pose,
						}}
						isLoadingOptions={isLoadingPreset}
						selectedValues={{
							background: selectedBackground,
							surface: selectedSurface,
							mood: selectedMood,
							effect: selectedEffect,
							person: selectedPerson,
							pose: selectedPose,
						}}
						onSelect={handleSelect}
						imageUrl={imageUrl}
						setImageUrl={setImageUrl}
						handleUpload={handleUpload}
						hasUserUpload={hasUserUpload}
						allowCustomBackground={allowCustomBackground}
						inputImage={inputImage}
						onClearInputImage={() => setInputImage(null)}
						onWebcamOpen={handleWebcamOpen}
					/>
				</ControlsAndOptionsWrapper>
			</ContentWrapper>
		</Dialog>
		</MobileDropdownProvider>
	);
};

// Styled Components
// tailwind: h-screen bg-gray-50 grid grid-rows-[1fr_auto]
const ContentWrapper = styled.div<{ $hasActivePanel: boolean }>`
	background-color: #f9fafb;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	position: relative;
	height: 100%;
	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		background-color: #f3f5f6;
		-webkit-overflow-scrolling: touch;
	}
`;

const ControlsAndOptionsWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;
